import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { fetchClanDirectoryDetail } from '../../../services/clans.api';
import { fetchElementsCatalog } from '../../../services/elements.api';
import { fetchClanPrestigeOverview, fetchClanPrestigeMe } from '../../../services/clansPrestige.api';
import { fetchClanInfraOverview } from '../../../services/clansInfra.api';
import { fetchClanTerritoriesOverview } from '../../../services/clansTerritories.api';
import HexMap from '../../../components/HexMap/HexMap';
import Legend from '../../../components/HexMap/Legend';
import ClanSubnav from './components/ClanSubnav';
import ClanKpis from './components/ClanKpis';
import './ViewClan.css';

const FLAG_CLAN_RESOURCES = true;
const FLAG_CLAN_PRESTIGE = true;
const FLAG_CLAN_INFRA = true;
const FLAG_CLAN_TERRITORIES = true;
const FLAG_CLAN_SUBNAV = true;
const FLAG_CLAN_KPIS = true;

function TechniqueCard({ t, eltLabel }) {
  const [expanded, setExpanded] = useState(false);

  // Rango: pestañas simuladas (E-D-C-B-A-S) empezando en el rango mínimo de la técnica
  const ALL_RANKS = useMemo(() => ['E','D','C','B','A','S'], []);
  const minRankIndex = useMemo(() => {
    const r = String(t?.rank || '').toUpperCase();
    const idx = ALL_RANKS.indexOf(r);
    return idx >= 0 ? idx : 0;
  }, [t?.rank, ALL_RANKS]);
  const RANKS = useMemo(() => ALL_RANKS.slice(minRankIndex), [ALL_RANKS, minRankIndex]);
  const showTabs = true; // Iteración 1: siempre mostramos pestañas

  // Rango inicial: usar t.rank si es válido; si no, primer tab (mínimo de la técnica)
  const initialRank = useMemo(() => {
    const r = String(t?.rank || '').toUpperCase();
    return RANKS.includes(r) ? r : (RANKS[0] || 'E');
  }, [t?.rank, RANKS]);

  const [selectedRank, setSelectedRank] = useState(initialRank);
  useEffect(() => { setSelectedRank(initialRank); }, [initialRank]);

  // Accesibilidad: refs y navegación con teclado
  const tabRefs = useRef([]);
  const onTabKeyDown = (e) => {
    const idx = RANKS.indexOf(selectedRank);
    if (idx < 0) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const next = idx > 0 ? idx - 1 : RANKS.length - 1;
      setSelectedRank(RANKS[next]);
      tabRefs.current[next]?.focus();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = (idx + 1) % RANKS.length;
      setSelectedRank(RANKS[next]);
      tabRefs.current[next]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      setSelectedRank(RANKS[0]);
      tabRefs.current[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      setSelectedRank(RANKS[RANKS.length - 1]);
      tabRefs.current[RANKS.length - 1]?.focus();
    }
  };

  // Tipos: soportar múltiples valores (array, separado por comas/puntos/|/+/&)
  const types = useMemo(() => {
    if (Array.isArray(t?.types) && t.types.length) {
      return t.types.map(String).filter(Boolean);
    }
    const value = t?.type;
    if (!value) return [];
    if (Array.isArray(value)) return value.map(String).filter(Boolean);
    return String(value)
      .split(/[,./|·]+|\s*&\s*|\s*\+\s*/g)
      .map((s) => s.trim())
      .filter(Boolean);
  }, [t?.types, t?.type]);

  const signature = !!(t?.is_signature ?? t?.isSignature ?? t?.signature);
  const subtitle =
    t?.subtitle || t?.altName || t?.alt_name || t?.name_es || t?.nameEs || t?.localizedName || null;
  const rankDisplay = t?.rank_label || t?.rank || null;

  // Id accesible del panel colapsable
  const contentId = useMemo(() => {
    const base = t?.techniqueId ?? t?.id ?? t?.name ?? 'tecnica';
    const slug = String(base).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return `tech-card-${slug}`;
  }, [t?.techniqueId, t?.id, t?.name]);

  const preview = useMemo(() => {
    const s = t?.description || '';
    if (!s) return '';
    return s.length > 300 ? s.slice(0, 300) + '…' : s;
  }, [t?.description]);

  // Umbrales: array de strings/objetos con label, o texto con saltos de línea
  const thresholds = useMemo(() => {
    if (Array.isArray(t?.thresholds)) {
      return t.thresholds
        .map((x) => {
          if (x == null) return null;
          if (typeof x === 'string') return x;
          if (typeof x === 'object') return x.label || x.name || x.key || null;
          return String(x);
        })
        .filter(Boolean);
    }
    if (typeof t?.thresholds_text === 'string') {
      return t.thresholds_text.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    }
    return [];
  }, [t?.thresholds, t?.thresholds_text]);

  // Atributos (si existen en datos)
  const damageEffect = t?.damageEffect ?? t?.damage ?? t?.effect ?? null;
  const actionCost = t?.movement_cost ?? t?.action_cost ?? t?.actionCost ?? null; // movimientos
  const price = t?.price ?? t?.price_ryous ?? t?.ryous ?? null;
  const spendText = t?.spend ?? t?.spendText ?? t?.gasto ?? t?.gasto_text ?? t?.gastoText ?? null;

// Propiedades de uso (Área, Distancia, Duración) con sinónimos
  const area = t?.area ?? t?.aoe ?? t?.area_text ?? null;
  const distance = t?.distance ?? t?.range ?? t?.rango ?? null;
  const duration = t?.duration ?? t?.time ?? t?.turns ?? null;
  const hasUseProps = !!(area || distance || duration);
  return (
    <article className={`clan-tech-card ${expanded ? 'is-expanded' : ''}`}>
      <div className="clan-tech-header">
        <div className="clan-tech-titles">
          <h4 className="clan-tech-name">{t.name}</h4>
          {subtitle && (
            <p className="clan-tech-subtitle">{subtitle}</p>
          )}
        </div>
        <div className="clan-tech-meta">
          {signature && <span className="badge badge-signature">Firma del Clan</span>}
          {types.map((tp) => (
            <span key={tp} className={`badge badge-type type-${tp.toLowerCase()}`}>{tp}</span>
          ))}
          {t.element && <span className={`badge elt elt-${t.element}`}>{(eltLabel && eltLabel(t.element)) || t.element}</span>}
          {(!showTabs && rankDisplay) && (
            <span className="badge badge-rank">Rango {rankDisplay}</span>
          )}
        </div>
        <button
          type="button"
          className="clan-tech-toggle clan-btn clan-btn-ghost"
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={() => setExpanded(e => !e)}
          title={expanded ? 'Ocultar detalles' : 'Mostrar detalles'}
        >
          {expanded ? 'Ocultar detalles' : 'Detalles'}
        </button>
      </div>

      <nav className="clan-tech-tabs" role="tablist" aria-label="Rangos de la técnica">
        {RANKS.map((r, i) => {
          const active = selectedRank === r;
          const tabId = `${contentId}-tab-${r}`;
          return (
            <button
              key={r}
              ref={el => (tabRefs.current[i] = el)}
              id={tabId}
              className={`clan-tech-tab ${active ? 'is-active' : ''}`}
              role="tab"
              aria-selected={active}
              aria-controls={contentId}
              tabIndex={active ? 0 : -1}
              onClick={() => setSelectedRank(r)}
              onKeyDown={onTabKeyDown}
              title={`Rango ${r}`}
              type="button"
            >
              {r}
            </button>
          );
        })}
      </nav>

      <div
        id={contentId}
        className="clan-tech-layout"
        role="tabpanel"
        aria-labelledby={`${contentId}-tab-${selectedRank}`}
        aria-hidden={!expanded}
      >
        {(actionCost != null || price != null || spendText || damageEffect) && (
          <div className="clan-tech-attrs" role="list">
            {actionCost != null && (
              <div className="tech-attr" role="listitem">
                <span className="tech-attr-label">Coste</span>
                <span className="tech-attr-value">
                  {actionCost} movimiento{Number(actionCost) === 1 ? '' : 's'}
                </span>
              </div>
            )}
            {price != null && (
              <div className="tech-attr" role="listitem">
                <span className="tech-attr-label">Precio</span>
                <span className="tech-attr-value">{price} ryous</span>
              </div>
            )}
            {spendText && (
              <div className="tech-attr" role="listitem">
                <span className="tech-attr-label">Gasto</span>
                <span className="tech-attr-value">{spendText}</span>
              </div>
            )}
            {damageEffect && (
              <div className="tech-attr" role="listitem">
                <span className="tech-attr-label">Daño/Efecto</span>
                <span className="tech-attr-value">{damageEffect}</span>
              </div>
            )}
          </div>
        )}
 
        {hasUseProps && (
          <div className="clan-tech-section clan-tech-props">
            <h5 className="clan-section-subtitle">Propiedades de uso</h5>
            <div className="clan-tech-props-grid">
              {area && (
                <div className="tech-attr">
                  <span className="tech-attr-label">Área</span>
                  <span className="tech-attr-value">{area}</span>
                </div>
              )}
              {distance && (
                <div className="tech-attr">
                  <span className="tech-attr-label">Distancia</span>
                  <span className="tech-attr-value">{distance}</span>
                </div>
              )}
              {duration && (
                <div className="tech-attr">
                  <span className="tech-attr-label">Duración</span>
                  <span className="tech-attr-value">{duration}</span>
                </div>
              )}
            </div>
          </div>
        )}
 
        <div className="clan-tech-cost">
          {(t?.cost?.chakra != null || t?.chakra_cost != null) && (
            <span>Chakra: {t?.cost?.chakra ?? t?.chakra_cost}</span>
          )}
          {(t?.cost?.stamina != null || t?.stamina_cost != null) && (
            <span>Stamina: {t?.cost?.stamina ?? t?.stamina_cost}</span>
          )}
          {(t?.cost?.notes || t?.cost_notes) && (
            <span>Notas: {t?.cost?.notes ?? t?.cost_notes}</span>
          )}
        </div>
 
        {!!thresholds.length && (
          <div className="clan-tech-section clan-tech-thresholds-section">
            <h5 className="clan-section-subtitle">Umbrales</h5>
            <ul className="clan-tech-thresholds">
              {thresholds.map((line, idx) => <li key={idx}>{line}</li>)}
            </ul>
          </div>
        )}
 
        <div className="clan-tech-desc">
          <p>{expanded ? (t.description || '') : preview}</p>
          {(t?.description && t.description.length > 300) && (
            <button className="clan-linklike" onClick={() => setExpanded(e => !e)}>
              {expanded ? 'Ver menos' : 'Ver más'}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

export default function ViewClan() {
  const { idOrSlug } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, data: null, notFound: false });

  // Catálogo de elementos (dinámico desde backend)
  const [eltMap, setEltMap] = useState({});
  useEffect(() => {
    let cancelled = false;
    async function loadElements() {
      try {
        const list = await fetchElementsCatalog();
        if (cancelled) return;
        const map = {};
        (Array.isArray(list) ? list : []).forEach((it) => {
          const code = String(it?.code ?? it?.name ?? '').toLowerCase();
          if (code) map[code] = it?.displayName ?? it?.display_name ?? it?.name ?? code;
        });
        setEltMap(map);
      } catch (e) {
        // fallback: mantener códigos si falla
      }
    }
    loadElements();
    return () => { cancelled = true; };
  }, []);
  const eltLabel = useMemo(() => (code) => eltMap?.[String(code).toLowerCase()] || code, [eltMap]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setState({ loading: true, error: null, data: null, notFound: false });
      try {
        const res = await fetchClanDirectoryDetail(idOrSlug);
        if (!cancelled) setState({ loading: false, error: null, data: res, notFound: false });
      } catch (err) {
        if (cancelled) return;
        if (err?.status === 404) {
          setState({ loading: false, error: null, data: null, notFound: true });
        } else {
          setState({ loading: false, error: err, data: null, notFound: false });
        }
      }
    }
    run();
    return () => { cancelled = true; };
  }, [idOrSlug]);

  // Datos base para recursos (siempre antes de cualquier return para cumplir reglas de Hooks)
  const c = state.data || {};
  const elements = Array.isArray(c.elements) ? c.elements : [];
  const initial = ((c?.name ?? '?').trim().charAt(0) || '?').toUpperCase();
  const resourcesOverview = useMemo(() => {
    const techniques = Array.isArray(c.techniques) ? c.techniques : [];
    const signatureCount =
      techniques.filter((t) => !!(t?.is_signature ?? t?.isSignature ?? t?.signature)).length;
    const counts = { common: null, rare: null, epic: null, legendary: null };
    const totalStacks = [counts.common, counts.rare, counts.epic, counts.legendary]
      .filter((n) => typeof n === 'number')
      .reduce((a, b) => a + b, 0);
    return {
      treasure: { totalRyo: null, lastTxAt: null },
      library: { techniquesCount: techniques.length, signatureCount },
      arsenal: { legendaryCount: null, consumableKinds: null },
      materials: { counts, lastGainAt: null, totalStacks },
      updatedAt: null,
    };
  }, [c.techniques]);

  // Líderes y utilidades de navegación
  const leaders = Array.isArray(c.leaders) ? c.leaders : [];
  const hasLeadersList = leaders.length > 0;
  const currentLeader = useMemo(
    () => leaders.find((ld) => (ld?.isCurrent || ld?.isCurrentLeader)) || null,
    [leaders]
  );

  const scrollToLeaders = useCallback(() => {
    const id = 'lideres';
    const el = document.getElementById(id);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const offset = 80;
    const top = window.scrollY + rect.top - offset;
    window.scrollTo({ top, behavior: 'smooth' });
    if (typeof window !== 'undefined' && window.history && typeof window.history.pushState === 'function') {
      try {
        window.history.pushState(null, '', `#${id}`);
      } catch {
        window.location.hash = `#${id}`;
      }
    } else if (typeof window !== 'undefined') {
      window.location.hash = `#${id}`;
    }
  }, []);

  // ====== Prestigio (placeholder: mocks) ======
  const [prestige, setPrestige] = useState({ loading: true, error: null, overview: null, me: null });
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!FLAG_CLAN_PRESTIGE) {
        setPrestige((p) => ({ ...p, loading: false }));
        return;
      }
      setPrestige({ loading: true, error: null, overview: null, me: null });
      try {
        const [overview, me] = await Promise.all([
          fetchClanPrestigeOverview(idOrSlug),
          fetchClanPrestigeMe(idOrSlug),
        ]);
        if (cancelled) return;
        setPrestige({ loading: false, error: null, overview, me });
      } catch (e) {
        if (cancelled) return;
        setPrestige({ loading: false, error: e, overview: null, me: null });
      }
    }
    load();
    return () => { cancelled = true; };
  }, [idOrSlug]);

  // ====== Infraestructura (placeholder: mocks) ======
  const [infra, setInfra] = useState({ loading: true, error: null, data: null });
  useEffect(() => {
    let cancelled = false;
    async function loadInfra() {
      if (!FLAG_CLAN_INFRA) {
        setInfra((p) => ({ ...p, loading: false }));
        return;
      }
      setInfra({ loading: true, error: null, data: null });
      try {
        const data = await fetchClanInfraOverview(idOrSlug);
        if (cancelled) return;
        setInfra({ loading: false, error: null, data });
      } catch (e) {
        if (cancelled) return;
        setInfra({ loading: false, error: e, data: null });
      }
    }
    loadInfra();
    return () => { cancelled = true; };
  }, [idOrSlug]);

  // ====== Territorios (placeholder: mocks) ======
  const [territories, setTerritories] = useState({ loading: true, error: null, data: null });
  useEffect(() => {
    let cancelled = false;
    async function loadTerritories() {
      if (!FLAG_CLAN_TERRITORIES) {
        setTerritories((p) => ({ ...p, loading: false }));
        return;
      }
      setTerritories({ loading: true, error: null, data: null });
      try {
        const data = await fetchClanTerritoriesOverview(idOrSlug);
        if (cancelled) return;
        setTerritories({ loading: false, error: null, data });
      } catch (e) {
        if (cancelled) return;
        setTerritories({ loading: false, error: e, data: null });
      }
    }
    loadTerritories();
    return () => { cancelled = true; };
  }, [idOrSlug]);

  // Selección de celda de territorio y refs para sincronizar lista
  const [selectedCell, setSelectedCell] = useState(null);
  const listRef = useRef(null);
  const cellRefs = useRef(new Map());

  const onMapCellClick = useCallback((cell) => {
    setSelectedCell(cell);
  }, []);

  useEffect(() => {
    if (!selectedCell) return;
    const key = `${Number(selectedCell.q)},${Number(selectedCell.r)}`;
    const el = cellRefs.current.get(key);
    if (el && typeof el.scrollIntoView === 'function') {
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } catch {}
    }
  }, [selectedCell]);

  if (state.loading) {
    return (
      <div className="clan-view view-clan parchment-scope">
        <div className="clan-loading" aria-live="polite">Cargando clan…</div>
      </div>
    );
  }

  if (state.notFound) {
    return (
      <div className="clan-view view-clan parchment-scope">
        <div className="clan-empty">
          <h3>Clan no encontrado</h3>
          <p>No se encontró el clan solicitado.</p>
          <div className="clan-actions">
            <button className="clan-btn clan-btn-ghost" onClick={() => navigate('/clanes')}>Volver</button>
          </div>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="clan-view view-clan parchment-scope">
        <div className="clan-error" role="alert" aria-live="assertive">
          <p>Error cargando el clan: {state.error?.message || String(state.error)}</p>
          <div className="clan-actions">
            <button className="clan-btn clan-btn-ghost" onClick={() => navigate('/clanes')}>Volver</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="clan-view view-clan parchment-scope">
      <section className="clan-hero">
        <div className="clan-hero-left">
          <div className="clan-emblem" aria-hidden={c?.emblemUrl ? undefined : true}>
            {c?.emblemUrl ? (
              <img
                className="clan-emblem-img"
                src={c.emblemUrl}
                alt={`Emblema de ${c.name}`}
                loading="lazy"
              />
            ) : (
              <div className="clan-emblem-fallback" title={`Emblema de ${c.name || 'Clan'}`}>
                {initial}
              </div>
            )}
          </div>
          <div className="clan-title-block">
            <span className="clan-eyebrow">氏 · Clan</span>
            <h1 className="clan-title">{c.name}</h1>
            <div className="clan-current-leader" aria-label="Información del líder actual">
              <span className="label">Líder actual:</span>
              {currentLeader ? (
                <span className="name">
                  {currentLeader.slug ? (
                    <Link to={`/personajes/${currentLeader.slug}`}>{currentLeader.name}</Link>
                  ) : (
                    currentLeader.name
                  )}
                </span>
              ) : (
                <span className="name clan-muted">Clan descabezado</span>
              )}
              <button
                type="button"
                className="clan-btn clan-btn-ghost"
                onClick={scrollToLeaders}
                disabled={!hasLeadersList}
                title={hasLeadersList ? 'Ver la lista completa de líderes' : 'No hay lista de líderes disponible'}
              >
                Ver lista completa
              </button>
            </div>
            <div className="clan-meta-line">
              {c?.affiliation?.name && (
                <span className="badge badge-affiliation">Afiliación: {c.affiliation.name}</span>
              )}
              {c?.rarity?.displayName && (
                <span className="badge badge-rarity">Rareza: {c.rarity.displayName}</span>
              )}
            </div>
            {!!elements.length && (
              <div className="clan-elts">
                {elements.map(el => (
                  <span key={el} className={`elt pill elt-${el}`}>{eltLabel(el)}</span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="clan-hero-actions">
          <Link className="clan-btn clan-btn-ghost" to="/clanes">Volver</Link>
        </div>
      </section>

      {FLAG_CLAN_SUBNAV && (
        <section className="clan-subnav-wrap" aria-label="Navegación de secciones">
          <ClanSubnav />
        </section>
      )}

      <div className="clan-content">
        {FLAG_CLAN_PRESTIGE && (
          <aside className="clan-left" aria-label="Columna izquierda: Prestigio del Clan">
            <section id="prestigio" className="clan-section clan-prestige">
              <h2 className="clan-section-title">Prestigio del Clan</h2>
              {prestige.loading ? (
                <p className="clan-loading" aria-live="polite">Cargando prestigio…</p>
              ) : prestige.error ? (
                <div className="clan-error" role="alert" aria-live="assertive">
                  <p>Error cargando prestigio: {prestige.error?.message || String(prestige.error)}</p>
                </div>
              ) : (
                <div className="clan-prestige-grid" role="region" aria-label="Prestigio del Clan">
                  <article className="prestige-card" aria-labelledby="prestige-top-title">
                    <h3 id="prestige-top-title" className="clan-res-title">Top de la temporada</h3>
                    {!(prestige.overview && Array.isArray(prestige.overview.top5) && prestige.overview.top5.length) ? (
                      <p className="clan-muted">Sin eventos de temporada.</p>
                    ) : (
                      <ol className="prestige-toplist">
                        {prestige.overview.top5.map((e, idx) => (
                          <li key={e.characterId || idx} className="prestige-row">
                            <span className={`medal m-${idx + 1}`}>{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : (idx + 1)}</span>
                            <span className="p-name">{e.name}</span>
                            <span className="p-points">{e.points} pts</span>
                          </li>
                        ))}
                      </ol>
                    )}
                  </article>

                  <article className="prestige-card" aria-labelledby="prestige-me-title">
                    <h3 id="prestige-me-title" className="clan-res-title">Tu posición</h3>
                    {prestige.me ? (
                      <div className="prestige-me">
                        <div className="row">
                          <span className="label">Puntos</span>
                          <span className="value">{prestige.me.points} pts</span>
                        </div>
                        <div className="row">
                          <span className="label">Rango</span>
                          <span className="value">#{prestige.me.rank}</span>
                        </div>
                        <div className="row">
                          <span className="label">Misiones</span>
                          <span className="value">{prestige.me.missions}</span>
                        </div>
                        <div className="row">
                          <span className="label">Semanal</span>
                          <span className="value">{prestige.me.weeklyUsed}/{prestige.me.weeklyCap}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="clan-muted">Inicia sesión como miembro para ver tu posición.</p>
                    )}
                  </article>
                </div>
              )}
            </section>
            {Array.isArray(c.leaders) && (
              <section id="lideres" className="clan-section">
                <h2 className="clan-section-title">Líderes</h2>
                {c.leaders.length === 0 ? (
                  <p className="clan-muted">El clan esta actualmente descabezado, cualquiera podría alzarse.</p>
                ) : (
                  <ul className="clan-leaders">
                    {c.leaders.map(ld => (
                      <li key={`${ld.characterId}-${ld.from || ld.since || 'na'}`}>
                        <div className="clan-leader-row">
                          <span className="clan-leader-name">
                            {ld.slug
                              ? <Link to={`/personajes/${ld.slug}`}>{ld.name}</Link>
                              : <>{ld.name}</>}
                          </span>
                          {(ld.isCurrent || ld.isCurrentLeader) && <span className="badge badge-current">Actual</span>}
                          <span className="clan-leader-period">
                            {((ld.from || ld.since) || (ld.to || ld.until)) ? `(${ld.from || ld.since || '¿?'} - ${ld.to || ld.until || 'presente'})` : null}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
          </aside>
        )}
        <div className="clan-main">
          {FLAG_CLAN_KPIS && (
            <section id="resumen" className="clan-section">
              <h2 className="clan-section-title">Resumen</h2>
              <ClanKpis
                treasureTotal={resourcesOverview?.treasure?.totalRyo ?? null}
                techniquesCount={resourcesOverview?.library?.techniquesCount ?? 0}
                signatureCount={resourcesOverview?.library?.signatureCount ?? 0}
                legendaryCount={resourcesOverview?.arsenal?.legendaryCount ?? null}
                materialStacks={resourcesOverview?.materials?.totalStacks ?? null}
                territoriesControlled={territories?.data?.summary?.controlledCount ?? 0}
                mePrestige={prestige?.me ?? null}
              />
            </section>
          )}
          {FLAG_CLAN_RESOURCES && (
            <section id="recursos" className="clan-section clan-resources">
              <h2 className="clan-section-title">Recursos</h2>
              <div className="clan-res-grid" role="region" aria-label="Recursos del Clan">
                <article className="clan-res-card res-treasure" aria-labelledby="res-treasure-title">
                  <div className="clan-res-header">
                    <div className="clan-res-icon" aria-hidden="true">💰</div>
                    <h3 id="res-treasure-title" className="clan-res-title">Tesoro del clan</h3>
                  </div>
                  <p className="clan-res-micro">Resumen del tesoro. Datos detallados próximamente.</p>
                  <div className="clan-res-metrics">
                    <div>
                      <dt>Total</dt>
                      <dd>
                        {resourcesOverview?.treasure?.totalRyo != null
                          ? `${resourcesOverview.treasure.totalRyo} Ryo`
                          : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt>Último movimiento</dt>
                      <dd>
                        {resourcesOverview?.treasure?.lastTxAt
                          ? new Date(resourcesOverview.treasure.lastTxAt).toLocaleDateString()
                          : 's/f'}
                      </dd>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="clan-btn clan-btn-ghost"
                    disabled
                    title="Próximamente"
                  >
                    Ver detalles
                  </button>
                </article>

                <article className="clan-res-card res-library" aria-labelledby="res-library-title">
                  <div className="clan-res-header">
                    <div className="clan-res-icon" aria-hidden="true">📜</div>
                    <h3 id="res-library-title" className="clan-res-title">Biblioteca</h3>
                  </div>
                  <p className="clan-res-micro">Técnicas registradas y firmas del clan.</p>
                  <div className="clan-res-metrics">
                    <div>
                      <dt>Técnicas</dt>
                      <dd>{resourcesOverview?.library?.techniquesCount ?? 0}</dd>
                    </div>
                    <div>
                      <dt>Firmas</dt>
                      <dd>{resourcesOverview?.library?.signatureCount ?? 0}</dd>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="clan-btn clan-btn-ghost"
                    disabled
                    title="Próximamente"
                  >
                    Ver detalles
                  </button>
                </article>

                <article className="clan-res-card res-arsenal" aria-labelledby="res-arsenal-title">
                  <div className="clan-res-header">
                    <div className="clan-res-icon" aria-hidden="true">🗡️</div>
                    <h3 id="res-arsenal-title" className="clan-res-title">Arsenal</h3>
                  </div>
                  <p className="clan-res-micro">Estado del arsenal del clan.</p>
                  <div className="clan-res-metrics">
                    <div>
                      <dt>Legendarias</dt>
                      <dd>
                        {resourcesOverview?.arsenal?.legendaryCount != null
                          ? resourcesOverview.arsenal.legendaryCount
                          : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt>Consumibles</dt>
                      <dd>
                        {resourcesOverview?.arsenal?.consumableKinds != null
                          ? resourcesOverview.arsenal.consumableKinds
                          : '—'}
                      </dd>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="clan-btn clan-btn-ghost"
                    disabled
                    title="Próximamente"
                  >
                    Ver detalles
                  </button>
                </article>

                <article className="clan-res-card res-materials" aria-labelledby="res-materials-title">
                  <div className="clan-res-header">
                    <div className="clan-res-icon" aria-hidden="true">🔷</div>
                    <h3 id="res-materials-title" className="clan-res-title">Materiales</h3>
                  </div>
                  <p className="clan-res-micro">Inventario de materiales del clan.</p>
                  <div className="clan-res-metrics">
                    <div>
                      <dt>Stacks totales</dt>
                      <dd>{resourcesOverview?.materials?.totalStacks ?? '—'}</dd>
                    </div>
                    <div>
                      <dt>Último ingreso</dt>
                      <dd>
                        {resourcesOverview?.materials?.lastGainAt
                          ? new Date(resourcesOverview.materials.lastGainAt).toLocaleDateString()
                          : 's/f'}
                      </dd>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="clan-btn clan-btn-ghost"
                    disabled
                    title="Próximamente"
                  >
                    Ver detalles
                  </button>
                </article>
              </div>
            </section>
          )}
          {c?.history && (
            <section className="clan-section">
              <h2 className="clan-section-title">Historia</h2>
              <div className="clan-panel clan-history">
                {c.history}
              </div>
            </section>
          )}

          {/* Prestigio movido a columna izquierda */}

          {/* Recursos movidos debajo de Prestigio */}

          {FLAG_CLAN_INFRA && (
            <section id="infra" className="clan-section clan-infra">
              <h2 className="clan-section-title">Infraestructura del Clan</h2>
              {infra.loading ? (
                <p className="clan-loading" aria-live="polite">Cargando infraestructura…</p>
              ) : infra.error ? (
                <div className="clan-error" role="alert" aria-live="assertive">
                  <p>Error cargando infraestructura: {infra.error?.message || String(infra.error)}</p>
                </div>
              ) : (
                <div className="clan-infra-grid" role="region" aria-label="Infraestructura del Clan">
                  {(infra.data?.buildings || []).map((b) => (
                    <article key={b.slug} className="infra-card" aria-labelledby={`infra-${b.slug}-title`}>
                      <div className="infra-header">
                        <div className="infra-icon" aria-hidden="true">{b.icon || '🏛️'}</div>
                        <h3 id={`infra-${b.slug}-title`} className="clan-res-title">{b.name}</h3>
                        <span className="infra-level-badge">Nivel {Number.isFinite(b.level) ? b.level : 0}</span>
                      </div>
                      <p className="infra-micro">Próximamente: efectos y mejoras activas.</p>
                      <div className="infra-actions">
                        <button
                          type="button"
                          className="clan-btn clan-btn-ghost"
                          disabled
                          title="Próximamente"
                        >
                          Mejorar
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          )}
          {FLAG_CLAN_TERRITORIES && (
            <section id="territorios" className="clan-section clan-territories">
              <h2 className="clan-section-title">Territorios</h2>
              {territories.loading ? (
                <p className="clan-loading" aria-live="polite">Cargando territorios…</p>
              ) : territories.error ? (
                <div className="clan-error" role="alert" aria-live="assertive">
                  <p>Error cargando territorios: {territories.error?.message || String(territories.error)}</p>
                </div>
              ) : (
                <div className="clan-territories-grid" role="region" aria-label="Territorios del Clan">
                  <div className="territories-map">
                    <HexMap
                      cells={territories.data?.cells || []}
                      hexSize={16}
                      showGrid={false}
                      onCellClick={onMapCellClick}
                    />
                    <Legend showClanPalette={false} />
                  </div>
                  <div className="territories-panel">
                    <div className="territories-summary">
                      <div><strong>Hexes controlados:</strong> {territories.data?.summary?.controlledCount ?? 0}</div>
                      <div><strong>En disputa:</strong> {territories.data?.summary?.disputedCount ?? 0}</div>
                      <div><strong>Upkeep diario:</strong> {territories.data?.summary?.upkeepPerDayRyo ?? 0} Ryo</div>
                      <div><strong>Estimación diaria:</strong> {territories.data?.summary?.estRyoPerDay ?? 0} Ryo · {territories.data?.summary?.estMaterialsPerDay ?? 0} materiales</div>
                    </div>

                    {selectedCell && (
                      <div className="territory-selected" role="region" aria-label="Celda seleccionada">
                        <div className="row">
                          <strong>{selectedCell.territoryName || selectedCell.territorySlug || `Hex (${selectedCell.q},${selectedCell.r})`}</strong>
                        </div>
                        <div className="row">
                          <span>Estado: {selectedCell.status}</span>
                          <span>Bioma: {selectedCell.biome}</span>
                        </div>
                        <div className="row">
                          <span>Clan: {selectedCell.clanName || selectedCell.clanSlug || '—'}</span>
                          <span>Desde: {selectedCell.since ? new Date(selectedCell.since).toLocaleDateString() : 's/f'}</span>
                        </div>
                        <div className="actions">
                          {selectedCell.territorySlug ? (
                            <Link className="clan-btn clan-btn-ghost" to={`/han/${selectedCell.territorySlug}`}>Ver territorio</Link>
                          ) : (
                            <button className="clan-btn clan-btn-ghost" disabled title="Sin identificador de territorio">Ver territorio</button>
                          )}
                        </div>
                      </div>
                    )}

                    <ul className="territories-list" ref={listRef}>
                      {(territories.data?.cells || []).map((cell) => {
                        const key = `${cell.q},${cell.r}`;
                        const isSelected =
                          !!(selectedCell &&
                          Number(selectedCell.q) === Number(cell.q) &&
                          Number(selectedCell.r) === Number(cell.r));
                        return (
                          <li
                            key={key}
                            ref={(el) => { if (el) cellRefs.current.set(key, el); }}
                            className={`territory-row ${isSelected ? 'is-selected' : ''}`}
                            onClick={() => setSelectedCell(cell)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setSelectedCell(cell);
                              }
                            }}
                            aria-current={isSelected ? 'true' : undefined}
                            title={cell.territoryName || cell.territorySlug || `(${cell.q},${cell.r})`}
                          >
                            <span className="t-coord">({cell.q},{cell.r})</span>
                            <span className="t-biome">{cell.biome}</span>
                            <span className={`t-status t-${String(cell.status).toLowerCase()}`}>{cell.status}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              )}
            </section>
          )}

          {Array.isArray(c.techniques) && (
            <section id="tecnicas" className="clan-section">
              <h2 className="clan-section-title">Técnicas</h2>
              {c.techniques.length === 0 ? (
                <p className="clan-muted">Sin técnicas registradas.</p>
              ) : (
                <div className="clan-tech-grid">
                  {c.techniques.map(t => <TechniqueCard key={t.techniqueId || t.name} t={t} eltLabel={eltLabel} />)}
                </div>
              )}
            </section>
          )}
        </div>
 
      </div>
    </div>
  );
}